import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

// LocalStorage key for persisting filters
const TASKS_FILTER_STORAGE_KEY = 'claude-tasks-filters';

interface TasksFilterState {
  statusFilter: string[];
  commandFilter: string;
  createdFilter: string;
  slugInput: string;
  activeSlugFilter: string;
  periodFilter: string[];
}

const getStoredFilters = (): Partial<TasksFilterState> => {
  try {
    const stored = localStorage.getItem(TASKS_FILTER_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('[ClaudeTasks] Failed to parse stored filters:', e);
  }
  return {};
};

const saveFiltersToStorage = (filters: TasksFilterState) => {
  try {
    localStorage.setItem(TASKS_FILTER_STORAGE_KEY, JSON.stringify(filters));
  } catch (e) {
    console.error('[ClaudeTasks] Failed to save filters:', e);
  }
};
import { useClaudeTasks, useClaudeTasksStatistics, useAvailablePeriods } from '@/hooks/useClaudeTasks';
import { ClaudeTaskStatus, ClaudeTaskCommand } from '@/types/claudeTask';
import { TaskStatusBadge } from '@/components/claude-tasks/TaskStatusBadge';
import { TaskCommandBadge } from '@/components/claude-tasks/TaskCommandBadge';
import { CreateTaskDialog } from '@/components/claude-tasks/CreateTaskDialog';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, ChevronLeft, ChevronRight, BarChart3, Clock, CheckCircle, XCircle, Timer } from 'lucide-react';

// Helper function to format duration
const formatDuration = (milliseconds: number): string => {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }
  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }
  if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
};

// Calculate task duration based on claimed_at
const getTaskDuration = (task: any): { duration: string; isRunning: boolean } | null => {
  if (!task.claimed_at) return null;

  const claimedAt = new Date(task.claimed_at);
  const isRunning = task.status === 'running' || task.status === 'pending';

  if (isRunning) {
    const elapsed = Date.now() - claimedAt.getTime();
    return { duration: formatDuration(elapsed), isRunning: true };
  } else {
    const updatedAt = new Date(task.updated_at);
    const elapsed = updatedAt.getTime() - claimedAt.getTime();
    return { duration: formatDuration(elapsed), isRunning: false };
  }
};

export default function ClaudeTasks() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Handler to update task priority
  const handlePriorityChange = async (taskId: string, newPriority: number) => {
    const { error } = await supabase
      .from('claude_tasks')
      .update({ priority: newPriority })
      .eq('id', taskId);

    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to update priority',
        variant: 'destructive',
      });
    } else {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['claude-tasks'] });
      toast({
        title: 'Priority updated',
        description: `Task priority set to ${newPriority}`,
      });
    }
  };

  // Load stored filters on mount
  const storedFilters = useMemo(() => getStoredFilters(), []);

  // Filter state - initialized from localStorage
  const [statusFilter, setStatusFilter] = useState<string[]>(storedFilters.statusFilter || []);
  const [commandFilter, setCommandFilter] = useState<string>(storedFilters.commandFilter || 'all');
  const [createdFilter, setCreatedFilter] = useState<string>(storedFilters.createdFilter || '7'); // Default: last 7 days
  const [slugInput, setSlugInput] = useState<string>(storedFilters.slugInput || '');
  const [activeSlugFilter, setActiveSlugFilter] = useState<string>(storedFilters.activeSlugFilter || '');
  const [periodFilter, setPeriodFilter] = useState<string[]>(storedFilters.periodFilter || []);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  // Persist filters to localStorage when they change
  useEffect(() => {
    saveFiltersToStorage({
      statusFilter,
      commandFilter,
      createdFilter,
      slugInput,
      activeSlugFilter,
      periodFilter,
    });
  }, [statusFilter, commandFilter, createdFilter, slugInput, activeSlugFilter, periodFilter]);

  // Calculate date for created_after filter (memoized to prevent query key changes)
  const createdAfter = useMemo(() => {
    if (createdFilter === 'all') return undefined;
    const days = parseInt(createdFilter);
    const date = new Date();
    if (days === 0) {
      // Today: set to start of today
      date.setHours(0, 0, 0, 0);
    } else {
      // Previous days: subtract days and round to start of day
      date.setDate(date.getDate() - days);
      date.setHours(0, 0, 0, 0);
    }
    return date.toISOString();
  }, [createdFilter]);

  // Fetch available periods
  const { data: availablePeriods } = useAvailablePeriods();

  // Fetch tasks with current filters
  const { data: tasksData, isLoading: isLoadingTasks, error: tasksError } = useClaudeTasks({
    slug: activeSlugFilter || undefined,
    command: commandFilter === 'all' ? undefined : commandFilter,
    status: statusFilter.length > 0 ? statusFilter : undefined,
    created_after: createdAfter,
    periods: periodFilter.length > 0 ? periodFilter : undefined,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
  });

  // Fetch statistics (load in background)
  // Note: We intentionally do NOT pass status filter to statistics query
  // because we always want to show the total counts for each status category
  const { data: statsData } = useClaudeTasksStatistics({
    slug: activeSlugFilter || undefined,
    command: commandFilter === 'all' ? undefined : commandFilter,
    // status is intentionally omitted to always show overall counts
    created_after: createdAfter,
    periods: periodFilter.length > 0 ? periodFilter : undefined,
  });

  const tasks = tasksData?.claudeTasks.tasks || [];
  const totalCount = tasksData?.claudeTasks.totalCount || 0;
  const hasNextPage = tasksData?.claudeTasks.hasNextPage || false;
  const hasPrevPage = tasksData?.claudeTasks.hasPrevPage || false;

  const stats = statsData || {
    totalTasks: 0,
    pending: 0,
    running: 0,
    completed: 0,
    failed: 0,
    byCommand: {
      run_diff: 0,
      scrape: 0,
      analyze: 0,
      run_extract: 0,
      fix_diff: 0,
    },
  };

  const handleSlugSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setActiveSlugFilter(slugInput);
      setCurrentPage(1); // Reset to first page
    }
  };

  const handleStatusToggle = (status: string) => {
    setStatusFilter(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
    setCurrentPage(1); // Reset to first page
  };

  const handlePeriodToggle = (period: string) => {
    setPeriodFilter(prev => {
      if (prev.includes(period)) {
        return prev.filter(p => p !== period);
      } else {
        return [...prev, period];
      }
    });
    setCurrentPage(1); // Reset to first page
  };

  const handleCommandChange = (value: string) => {
    setCommandFilter(value);
    setCurrentPage(1);
  };

  const handleCreatedFilterChange = (value: string) => {
    setCreatedFilter(value);
    setCurrentPage(1);
  };

  const handlePrevPage = () => {
    if (hasPrevPage) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleTaskClick = (taskId: string) => {
    navigate(`/tasks/${taskId}`);
  };

  const handleTaskCreated = () => {
    // Invalidate queries to refresh the tasks list
    queryClient.invalidateQueries(['claudeTasks']);
    queryClient.invalidateQueries(['claudeTasksStatistics']);
  };

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Claude Tasks</h1>
        <CreateTaskDialog onTaskCreated={handleTaskCreated} />
      </div>

      {/* Filters that affect card numbers - Above cards */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Command Filter */}
            <div>
              <label className="text-sm font-medium mb-2 block">Command</label>
              <Select value={commandFilter} onValueChange={handleCommandChange}>
                <SelectTrigger>
                  <SelectValue placeholder="All Commands" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Commands</SelectItem>
                  <SelectItem value={ClaudeTaskCommand.RUN_DIFF}>Run Diff</SelectItem>
                  <SelectItem value={ClaudeTaskCommand.SCRAPE}>Scrape</SelectItem>
                  <SelectItem value={ClaudeTaskCommand.ANALYZE}>Analyze</SelectItem>
                  <SelectItem value={ClaudeTaskCommand.EXTRACT}>Extract</SelectItem>
                  <SelectItem value={ClaudeTaskCommand.FIX_DIFF}>Fix Diff</SelectItem>
                  <SelectItem value={ClaudeTaskCommand.RUN_ADD}>Add Page</SelectItem>
                  <SelectItem value={ClaudeTaskCommand.RUN_ADD_PAGE}>Add Sub-Page</SelectItem>
                  <SelectItem value={ClaudeTaskCommand.TAG_PAGE}>Tag Page</SelectItem>
                  <SelectItem value={ClaudeTaskCommand.DISCOVERY}>Discovery</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Created Filter */}
            <div>
              <label className="text-sm font-medium mb-2 block">Created</label>
              <Select value={createdFilter} onValueChange={handleCreatedFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Today" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Today</SelectItem>
                  <SelectItem value="2">Last 48 hours</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Slug Search */}
            <div>
              <label className="text-sm font-medium mb-2 block">Company Slug</label>
              <div className="relative">
                <Input
                  placeholder="Search by slug (press Enter)"
                  value={slugInput}
                  onChange={(e) => setSlugInput(e.target.value)}
                  onKeyDown={handleSlugSearch}
                  className="pr-8"
                />
                <Search className="absolute right-2 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>
          </div>

          {activeSlugFilter && (
            <div className="mt-4">
              <Badge variant="secondary" className="gap-2">
                Filtering by: {activeSlugFilter}
                <button
                  onClick={() => {
                    setActiveSlugFilter('');
                    setSlugInput('');
                  }}
                  className="ml-1 hover:text-red-500"
                >
                  ×
                </button>
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Running</CardTitle>
            <Clock className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.running}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Timer className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completed}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.failed}</div>
          </CardContent>
        </Card>
      </div>

      {/* Status + Period Filters - Below cards */}
      <Card className="mb-6">
        <CardContent className="pt-6 space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              Status <span className="text-gray-500 font-normal">({stats.totalTasks} total)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {Object.values(ClaudeTaskStatus).map(status => (
                <Badge
                  key={status}
                  variant={statusFilter.includes(status) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => handleStatusToggle(status)}
                >
                  {status}
                </Badge>
              ))}
            </div>
          </div>

          {availablePeriods && availablePeriods.filter(p => p.includes('W')).length > 0 && (
            <div>
              <label className="text-sm font-medium mb-2 block">
                Period
                {periodFilter.length > 0 && (
                  <button
                    className="ml-2 text-xs text-gray-400 hover:text-red-500 font-normal"
                    onClick={() => setPeriodFilter([])}
                  >
                    clear
                  </button>
                )}
              </label>
              <div className="flex flex-wrap gap-2">
                {availablePeriods.filter(p => p.includes('W')).slice(0, 12).map(period => (
                  <Badge
                    key={period}
                    variant={periodFilter.includes(period) ? 'default' : 'outline'}
                    className="cursor-pointer font-mono"
                    onClick={() => handlePeriodToggle(period)}
                  >
                    {period}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tasks Table */}
      <Card>
        <CardContent className="pt-6">
          {tasksError ? (
            <div className="text-center py-8 text-red-500">
              <p className="font-semibold">Error loading tasks</p>
              <p className="text-sm mt-2">{tasksError instanceof Error ? tasksError.message : 'Unknown error'}</p>
              <pre className="text-xs mt-2 text-left bg-gray-100 p-2 rounded overflow-auto max-h-40">
                {JSON.stringify(tasksError, null, 2)}
              </pre>
            </div>
          ) : isLoadingTasks ? (
            <div className="text-center py-8">Loading tasks...</div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No tasks found</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Command</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tasks.map((task) => {
                    const durationInfo = getTaskDuration(task);
                    const isPending = task.status === 'pending';
                    return (
                      <TableRow
                        key={task.id}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleTaskClick(task.id)}
                      >
                        <TableCell>
                          <TaskCommandBadge command={task.command} />
                        </TableCell>
                        <TableCell>
                          <TaskStatusBadge status={task.status} />
                        </TableCell>
                        <TableCell>
                          {isPending ? (
                            <Select
                              value={String(task.priority || 5)}
                              onValueChange={(value) => handlePriorityChange(task.id, parseInt(value))}
                            >
                              <SelectTrigger
                                className="w-16 h-7 text-xs"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent onClick={(e) => e.stopPropagation()}>
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((p) => (
                                  <SelectItem key={p} value={String(p)}>
                                    {p}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-sm text-gray-500">{task.priority || 5}</span>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{task.slug}</TableCell>
                        <TableCell className="text-sm">
                          {durationInfo ? (
                            <div className="flex items-center gap-1">
                              {durationInfo.isRunning && <Timer className="h-3 w-3 text-blue-500 animate-pulse" />}
                              <span className={durationInfo.isRunning ? 'text-blue-600 font-medium' : 'text-gray-500'}>
                                {durationInfo.duration}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {format(new Date(task.created_at), 'MMM d, yyyy HH:mm')}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {format(new Date(task.updated_at), 'MMM d, yyyy HH:mm')}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="text-sm text-gray-500">
                  Showing {(currentPage - 1) * pageSize + 1} to{' '}
                  {Math.min(currentPage * pageSize, totalCount)} of {totalCount} tasks
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={!hasPrevPage}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-gray-500">Page {currentPage}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={!hasNextPage}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
